import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { loginWithMicrosoft, User } from "../services/api";
import { useBranding } from "../hooks/useBranding";
import styles from "./LoginPage.module.css";
import { brandingMap } from "../config/brandingMap";
import apiClient from "../services/apiClient";
import { toast } from "react-toastify";

interface Props {
  onLogin: (user: User) => void;
}

export const LoginPage: React.FC<Props> = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [showMSLogin, setShowMSLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { instance, inProgress } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const fromPath = (location.state as any)?.from?.pathname || "/";

  const handleLogin = async () => {
    try {
      setMicrosoftLoading(true);
      setLoading(true);
      setError(null);

      // Define login request with correct scopes
      const loginRequest = {
        scopes: ["user.read"],
        prompt: "select_account",
        // Add redirectUri explicitly to ensure it doesn't clear the hash
        redirectUri: window.location.origin + window.location.pathname,
      };

      console.log("Starting MSAL login process...");

      // Save current state before login
      const currentState = {
        fromPath,
        redirectAfterLogin: sessionStorage.getItem("redirectAfterLogin"),
      };
      sessionStorage.setItem("msalAuthState", JSON.stringify(currentState));

      // Use loginPopup with proper error handling
      const response = await instance.loginPopup(loginRequest);
      console.log("MSAL login successful, token acquired");

      // Verify we have a valid access token
      if (!response || !response.accessToken) {
        throw new Error("No access token received from authentication");
      }

      console.log("Sending token to backend...");
      const backend = await loginWithMicrosoft(response.accessToken);
      console.log("Backend login response:", backend);

      if (backend.authenticated) {
        console.log(
          "Login successful, navigating to:",
          backend.redirectUrl || fromPath
        );

        // Check if user data is available in the response
        if (backend.user) {
          // Create a complete user object by combining user data and roles
          const userWithRoles = {
            ...backend.user,
            roles: backend.roles || [],
          };
          onLogin(userWithRoles);
        }

        // Retrieve the saved state (in case it was lost during auth)
        let savedState;
        try {
          const stateData = sessionStorage.getItem("msalAuthState");
          if (stateData) {
            savedState = JSON.parse(stateData);
          }
        } catch (e) {
          console.warn("Could not retrieve saved state:", e);
        }

        // Priorität:
        // 1. Die redirectUrl vom Backend, wenn vorhanden
        // 2. Der gespeicherte Wert aus sessionStorage
        // 3. Der Wert aus dem gespeicherten State
        // 4. Der Ursprungspfad aus dem Router-State
        // 5. Der Standard-Pfad '/'
        const redirectTarget =
          backend.redirectUrl ||
          sessionStorage.getItem("redirectAfterLogin") ||
          savedState?.redirectAfterLogin ||
          savedState?.fromPath ||
          fromPath ||
          "/";

        console.log("Final redirect target:", redirectTarget);

        // Clear the saved state after use
        sessionStorage.removeItem("msalAuthState");

        // Echtes Hard-Redirect durchführen für vollständigen Seitenneuladen
        window.location.href = redirectTarget;
      } else {
        setError("Backend authentication failed");
      }
    } catch (err: any) {
      console.error("Login error:", err);
      // Handle specific MSAL errors
      if (err instanceof InteractionRequiredAuthError) {
        setError("Authentication requires additional interaction");
      } else if (
        err.errorCode === "empty_hash_error" ||
        err.errorMessage?.includes("hash value cannot be processed")
      ) {
        setError("Authentication process was interrupted. Please try again.");

        // Force re-initialization of MSAL
        try {
          await instance.handleRedirectPromise();
        } catch (e) {
          console.warn("Error handling redirect:", e);
        }
      } else {
        setError(err instanceof Error ? err.message : "Authentication failed");
      }
    } finally {
      setLoading(false);
      setMicrosoftLoading(false);
    }
  };

  const handleMagicLink = async () => {
    setMagicLinkLoading(true);
    setLoading(true);
    setError(null);

    try {
      const redirect_url = window.location.origin; // z. B. https://localhost:3000
      const forward_url = sessionStorage.getItem("redirectAfterLogin") || "/";

      await apiClient.post("/api/v2/web/auth/magic-link/request", {
        email,
        URL: redirect_url,
        forward_url,
      });

      // Success notification with both Toast and Alert
      toast.success("✅ Magic link sent! Please check your email.", {
        position: "top-center",
        autoClose: 5000,
      });
    } catch (err: any) {
      console.error("Magic link error:", err);
      
      // Granulare Fehlerbehandlung
      let errorMessage = 'Failed to send magic link';
      
      if (err.response) {
        // Server hat mit einem Fehlercode geantwortet
        const status = err.response.status;
        const data = err.response.data;
        
        if (status === 400) {
          errorMessage = data?.detail || 'Invalid email address or request.';
        } else if (status === 404) {
          errorMessage = 'Email address not found. Please check your email or contact support.';
        } else if (status === 429) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (status === 500) {
          errorMessage = data?.detail || 'Server error. Please try again later.';
        } else if (data?.detail) {
          errorMessage = data.detail;
        } else {
          errorMessage = `Error (${status}). Please try again.`;
        }
      } else if (err.request) {
        // Request wurde gesendet, aber keine Antwort erhalten
        errorMessage = 'No response from server. Please check your internet connection.';
      } else if (err.message) {
        // Fehler beim Setup des Requests
        if (err.message.includes('Network Error')) {
          errorMessage = 'Network error. Please check your internet connection.';
        } else if (err.message.includes('timeout')) {
          errorMessage = 'Request timeout. Please try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      
      // Error notification
      toast.error(`❌ ${errorMessage}`, {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
      setMagicLinkLoading(false);
    }
  };

  return (
    <div className={styles.loginWrapper}>
      <div className={styles.loginContainer}>
        <h1>Login</h1>
        <div className={styles.loginForm} style={{ width: "80%" }}>
          <input
            type="email"
            id="email"
            className={styles.loginEmail}
            value={email}
            onChange={(e) => {
              const v = e.target.value;
              setEmail(v);
              setShowMSLogin(v.endsWith("@ypog.law"));
            }}
            disabled={loading}
            required
            placeholder="Type in your email"
            autoComplete="email"
          />

          {error && (
            <div
              className={styles.errorMessage}
            >
              {error}
            </div>
          )}

          <div className={styles.loginFormButtons}>
          <button
            onClick={handleMagicLink}
            disabled={loading || !email}
            className="btn btn-primary"
          >
            {magicLinkLoading && (
              <span
                id="magic-link-spinner"
                className="spinner-border spinner-border-sm me-2"
              ></span>
            )}
            {magicLinkLoading ? "Sending..." : "Send Log-In Link"}
          </button>
            
          {showMSLogin && (
            <button
              onClick={handleLogin}
              disabled={loading || inProgress !== InteractionStatus.None}
              className="btn btn-primary"
            >
              {microsoftLoading && (
                <span
                  id="microsoft-login-spinner"
                  className="spinner-border spinner-border-sm me-2"
                ></span>
              )}
              {microsoftLoading ? "Logging in..." : "Login with Microsoft 365"}
            </button>
          )}
</div>


          {useBranding.name === "MatchMaker" ? (
            <div className={styles.footer}>
              Not a member?{" "}
              <a href="mailto:events@ypog.law?subject=MatchMaker%20%7C%20Support%20Request%20">
                Reach out to the YPOG Events-Team!
              </a>
            </div>
          ) : (
            <div className={styles.footer}>
              Not a member?{" "}
              <a href="mailto:events@ypog.law?subject=Managing%20Partners'%20Speed%20Dating%20%7C%20Support%20Request">
                Reach out to the YPOG Events-Team!
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
